import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const ALLOWED = ['principal', 'admin', 'admin1'];
const BASE = `${API_BASE_URL}/api/diary-settings`;
const DEFAULTS = { baseScale: 1.5, subjectTitleSize: '20px', contentFontSize: '17px' };

/**
 * Principal / Admin / Admin1 only: (1) live diary font scaler, (2) per-class subject manager.
 * Both write to the server, so every teacher's diary (screen + generated image) follows.
 */
export default function DiaryManagerPanel({ user }) {
  const allowed = ALLOWED.includes(user?.role);
  const headers = { 'x-user-role': user?.role || '', 'x-user-name': encodeURIComponent(user?.name || user?.email || '') };

  const [font, setFont] = useState(DEFAULTS);
  const [savedFont, setSavedFont] = useState(DEFAULTS);
  const [classes, setClasses] = useState([]);
  const [className, setClassName] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState(null); // { name, displayName }
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const notify = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };
  const errText = (e) => e.response?.data?.error || e.message;

  useEffect(() => {
    if (!allowed) return;
    axios.get(BASE).then((r) => {
      const f = r.data?.diaryFontSettings || DEFAULTS;
      setFont(f); setSavedFont(f);
    }).catch(() => {});
    axios.get(`${API_BASE_URL}/api/classes`).then((r) => {
      const names = (r.data || []).map((c) => c.name).filter(Boolean);
      setClasses([...new Set(names)]);
    }).catch(() => notify('error', 'Could not load classes'));
    // eslint-disable-next-line
  }, [allowed]);

  const loadSubjects = async (cls) => {
    if (!cls) { setSubjects([]); return; }
    try {
      const r = await axios.get(`${BASE}/subjects/${encodeURIComponent(cls)}`, { headers });
      setSubjects(r.data.subjects || []);
    } catch (e) { notify('error', errText(e)); }
  };
  useEffect(() => { setEditing(null); loadSubjects(className); /* eslint-disable-next-line */ }, [className]);

  if (!allowed) return null;

  // ── Font manager ──
  const scale = Number(font.baseScale);
  const dirty = JSON.stringify(font) !== JSON.stringify(savedFont);
  const rel = scale / DEFAULTS.baseScale;
  const px = (v, fb) => `${Math.round((parseFloat(v) || fb) * rel * 10) / 10}px`;

  const saveFont = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${BASE}/font`, font, { headers });
      setFont(r.data.diaryFontSettings); setSavedFont(r.data.diaryFontSettings);
      notify('ok', 'Diary font size saved for all teachers');
    } catch (e) { notify('error', errText(e)); } finally { setBusy(false); }
  };

  // ── Subject manager ──
  const run = async (fn, okMsg) => {
    setBusy(true);
    try { const r = await fn(); setSubjects(r.data.subjects || []); notify('ok', okMsg); return true; }
    catch (e) { notify('error', errText(e)); return false; }
    finally { setBusy(false); }
  };
  const base = () => `${BASE}/subjects/${encodeURIComponent(className)}`;
  const addSubject = async () => {
    if (!newName.trim()) return;
    if (await run(() => axios.post(base(), { displayName: newName }, { headers }), 'Subject added - visible in the diary now')) setNewName('');
  };
  const saveEdit = async () => {
    if (await run(() => axios.put(`${base()}/${encodeURIComponent(editing.name)}`, { displayName: editing.displayName }, { headers }), 'Subject updated')) setEditing(null);
  };
  const removeSubject = (s) => {
    if (!window.confirm(`Remove "${s.displayName}" from Class ${className}? It disappears from the teacher's diary.`)) return;
    run(() => axios.delete(`${base()}/${encodeURIComponent(s.name)}`, { headers }), 'Subject removed');
  };

  const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 16, marginBottom: 14 };
  const btn = (bg) => ({ background: bg, color: '#fff', border: 0, borderRadius: 10, padding: '10px 16px', fontWeight: 700, fontSize: 14, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 });
  const input = { flex: 1, minWidth: 0, padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 16 };

  return (
    <div style={{ background: '#f8fafc', border: '2px solid #c7d2fe', borderRadius: 16, padding: 16 }}>
      <h3 style={{ margin: '0 0 4px', color: '#3730a3' }}>📒 Teacher Diary Controls</h3>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b' }}>Font size and subjects apply to every teacher's diary immediately.</p>
      {toast && (
        <div role="status" style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 10, fontWeight: 600, fontSize: 13, background: toast.type === 'ok' ? '#dcfce7' : '#fee2e2', color: toast.type === 'ok' ? '#166534' : '#991b1b' }}>{toast.message}</div>
      )}

      <section style={card}>
        <h4 style={{ margin: '0 0 8px', color: '#1e40af' }}>🔠 Diary Font Size</h4>
        <label htmlFor="diary-scale" style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>
          Scale: {scale.toFixed(2)}x ({Math.round((scale - 1) * 100)}% larger than the old size)
        </label>
        <input id="diary-scale" type="range" min="1" max="2.2" step="0.05" value={scale}
          onChange={(e) => setFont({ ...font, baseScale: Number(e.target.value) })} style={{ width: '100%', margin: '8px 0' }} />
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <label style={{ fontSize: 13, color: '#475569' }}>Subject title (px)
            <input type="number" min="12" max="48" value={parseFloat(font.subjectTitleSize) || ''} style={{ ...input, display: 'block', width: 110, marginTop: 4 }}
              onChange={(e) => setFont({ ...font, subjectTitleSize: `${e.target.value}px` })} />
          </label>
          <label style={{ fontSize: 13, color: '#475569' }}>Content (px)
            <input type="number" min="10" max="40" value={parseFloat(font.contentFontSize) || ''} style={{ ...input, display: 'block', width: 110, marginTop: 4 }}
              onChange={(e) => setFont({ ...font, contentFontSize: `${e.target.value}px` })} />
          </label>
        </div>
        <div style={{ border: '1px dashed #94a3b8', borderRadius: 10, padding: 12, background: '#f9fafb', marginBottom: 10 }}>
          <div style={{ fontSize: px(font.subjectTitleSize, 20), fontWeight: 700, color: '#1e40af' }}>📚 Mathematics</div>
          <div style={{ fontSize: px(font.contentFontSize, 17), color: '#334155' }}>Exercise 4.2, Q1 to Q5. Revise the tables.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" disabled={busy || !dirty} onClick={saveFont} style={btn('#2563eb')}>💾 Save for all teachers</button>
          <button type="button" disabled={busy} onClick={() => setFont(DEFAULTS)} style={btn('#64748b')}>↺ Reset to default</button>
        </div>
      </section>

      <section style={card}>
        <h4 style={{ margin: '0 0 8px', color: '#1e40af' }}>📚 Class Subjects</h4>
        <select value={className} onChange={(e) => setClassName(e.target.value)} style={{ ...input, width: '100%', marginBottom: 10 }}>
          <option value="">Select a class…</option>
          {classes.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {className && (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <input value={newName} maxLength={60} placeholder="New subject, e.g. Drawing" style={input}
                onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addSubject()} />
              <button type="button" disabled={busy || !newName.trim()} onClick={addSubject} style={btn('#16a34a')}>＋ Add</button>
            </div>
            {subjects.length === 0 && <div style={{ fontSize: 13, color: '#64748b' }}>No subjects yet for Class {className}.</div>}
            {subjects.map((s) => (
              <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                {editing?.name === s.name ? (
                  <>
                    <input autoFocus value={editing.displayName} maxLength={60} style={input}
                      onChange={(e) => setEditing({ ...editing, displayName: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && saveEdit()} />
                    <button type="button" disabled={busy} onClick={saveEdit} style={btn('#2563eb')}>Save</button>
                    <button type="button" onClick={() => setEditing(null)} style={btn('#64748b')}>Cancel</button>
                  </>
                ) : (
                  <>
                    <span style={{ flex: 1, minWidth: 0, fontWeight: 600, color: '#0f172a', overflowWrap: 'anywhere' }}>{s.displayName}</span>
                    <button type="button" onClick={() => setEditing({ name: s.name, displayName: s.displayName })} style={btn('#f59e0b')}>✏️ Edit</button>
                    <button type="button" disabled={busy} onClick={() => removeSubject(s)} style={btn('#dc2626')}>🗑️ Remove</button>
                  </>
                )}
              </div>
            ))}
          </>
        )}
      </section>
    </div>
  );
}

